import { useTranslations } from "../../contexts/Translation";
import Section from "../Section";
import Responses from "./Responses";
import { useSurvey } from "../../contexts/Survey";
import { useApiData } from "../../contexts/ApiData";
import SystemMessage from "../../components/SystemMessage/Index";
import { useEffect, useMemo, useState } from "react";
import SurveyForm from "../Forms/SurveyForm";
import Invites from "./Invites/Invites";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import { CategoryScores, isFollowUpRecommended } from "../Dashboard/insights";
import { Response, Survey } from "../../types";
import Tabs from "../Tabs/Tabs";
import SurveyHero from "./SurveyHero";
import Summary from "./Summary";
import { Reflections } from "./Reflections";
import Comments from "../Comments/List";
import CoachCard from "./CoachCard";
import QuoteCard from "./QuoteCard";
import AccessCard from "./AccessCard";
import { categoryResults, earlierSurveys, lowestStreakFor, toCategoryScores } from "./surveyHistory";
import styles from "./Details.module.css";

const STREAK_LIMIT = 5;

const SurveyDetails = () => {
  const t = useTranslations("surveys");
  const { get } = useApiData();
  const { survey, isOwner, canEdit } = useSurvey();
  const [isEdit, setIsEdit] = useState(false);
  const [responses, setResponses] = useState<Response[] | null>(null);
  const [allSurveys, setAllSurveys] = useState<Survey[]>([]);
  const surveyId = survey?.id;
  const status = survey?.status;

  useEffect(() => {
    if (!surveyId || status === "upcoming") {
      return;
    }
    const call = async () => {
      const [responseList, surveyList] = await Promise.all([get(`/response/${surveyId}/responses`), get(`/survey`)]);
      setResponses(responseList || []);
      setAllSurveys(surveyList || []);
    };
    call();
  }, [get, surveyId, status]);

  const earlier = useMemo(() => (survey ? earlierSurveys(allSurveys, survey).slice(0, STREAK_LIMIT) : []), [allSurveys, survey]);

  // Category scores for the earlier surveys, for the previous score and the lowest streak.
  const scoresById = useMemo<Record<string, CategoryScores>>(
    () => Object.fromEntries(earlier.map((s) => [s.id, s.categoryScores ?? {}])),
    [earlier],
  );

  if (!survey) {
    return <SystemMessage message='loading' />;
  }

  const hero = <SurveyHero isEdit={isEdit} onToggleEdit={() => setIsEdit(!isEdit)} />;
  const access = <AccessCard onShare={() => setIsEdit(true)} />;

  if (isEdit) {
    return (
      <>
        <Section>{hero}</Section>
        <Section span={8} variant='card'>
          <SurveyForm isEdit onSuccess={() => setIsEdit(false)} />
        </Section>
      </>
    );
  }

  if (survey.status === "upcoming") {
    return (
      <>
        <Section>{hero}</Section>
        <Section span={8}>
          <Invites surveyId={survey.id} />
        </Section>
        <Section span={4}>{access}</Section>
      </>
    );
  }

  // Only the owner and the person the survey is on behalf of may ask the coaches; the API answers 403
  // to supporters, groups and the organisation. isOwner is the owner, canEdit the person on behalf of.
  const canAskCoach = isOwner || canEdit;
  const previous = earlier[0] || null;
  const streak = responses ? lowestStreakFor(toCategoryScores(categoryResults(responses).questions), earlier, scoresById) : null;

  const tabs = responses
    ? [
        {
          key: "summary",
          label: t("tabSummary"),
          content: <Summary responses={responses} survey={survey} previous={previous} previousScores={previous ? scoresById[previous.id] : undefined} streak={streak} />,
        },
        { key: "responses", label: t("tabResponses", responses.length), content: <Responses responses={responses} candidate={survey.candidate} /> },
        {
          key: "reflections",
          label: t("tabReflections"),
          content: (
            <div className={styles.panelCard}>
              <Reflections />
            </div>
          ),
        },
        {
          key: "notes",
          label: t("tabNotes"),
          content: (
            <div className={styles.panelCard}>
              <Comments surveyId={survey.id} enableAiComment={survey.status === "finished" && canAskCoach} />
            </div>
          ),
        },
      ]
    : null;

  return (
    <>
      <Section>
        {hero}
        {isFollowUpRecommended(survey) && (
          <InfoMessage type='info' variant='quiet'>
            {t("followUpMeetingRecommendation")}
          </InfoMessage>
        )}
      </Section>
      <Section span={8}>{tabs ? <Tabs label={t("tabsLabel")} tabs={tabs} /> : <SystemMessage message='loading' />}</Section>
      {/* Only once the responses are loaded, so an empty side column leaves no gap. */}
      {responses && (
        <Section span={4}>
          <CoachCard surveyId={survey.id} status={survey.status} canAskCoach={canAskCoach} />
          <QuoteCard responses={responses} />
          {access}
        </Section>
      )}
    </>
  );
};

export default SurveyDetails;
